/**
 * Shared bits for the customer sales-tax exemption certificate feature
 * (Kansas ST-28D / ST-28A / ST-28M). API: jobq-api tax_certificates.py +
 * /customers/{id}/tax_certificates. Used by TaxCertificatesCard (customer
 * page), the Sales Tax Report (InvoicesModule) and the public portal page.
 */
import config from "../config.js";
import { detailToString } from "./apiError";

export const API_BASE = config.API_BASE_URL;

/** Derived per-customer status → label + Bootstrap badge class. */
export const CUSTOMER_STATUS = {
  on_file:         { label: "Certificate on file",          badge: "bg-success" },
  not_required:    { label: "Taxable — no certificate needed", badge: "bg-secondary" },
  pending_review:  { label: "Received — needs review",       badge: "bg-info text-dark" },
  requested:       { label: "Requested — waiting on customer", badge: "bg-warning text-dark" },
  expired:         { label: "Certificate expired",           badge: "bg-danger" },
  request_expired: { label: "Request link expired",          badge: "bg-danger" },
  missing:         { label: "No certificate",                badge: "bg-danger" },
};

/** Per-row lifecycle status → badge class. */
export const ROW_STATUS = {
  requested: "bg-warning text-dark",
  received:  "bg-info text-dark",
  verified:  "bg-success",
  rejected:  "bg-danger",
  revoked:   "bg-dark",
  cancelled: "bg-secondary",
};

export const TAX_TREATMENTS = [
  { value: "unknown", label: "Not classified" },
  { value: "exempt",  label: "Exempt — certificate required" },
  { value: "taxable", label: "Taxable — we charge sales tax" },
];

export function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export async function apiJson(url, opts = {}) {
  const res = await fetch(url, { headers: { "Content-Type": "application/json" }, ...opts });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailToString(body.detail) || `HTTP ${res.status}`);
  return body;
}

/** Browser → S3 presigned PUT with a progress callback (same shape as PodPanel). */
export function putToS3WithProgress(putUrl, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", putUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new Error(`Storage refused the upload (HTTP ${xhr.status}).`)));
    xhr.onerror = () => reject(new Error("The upload to storage failed (network)."));
    xhr.send(file);
  });
}

let _formsCache = null;
/** Form catalog (codes, titles, KDOR links, who-uses-it) — cached per page load. */
export async function loadFormCatalog() {
  if (_formsCache) return _formsCache;
  _formsCache = await apiJson(`${API_BASE}/tax_certificates/forms`);
  return _formsCache;
}

/** Ask the API for a presigned link and open the file in a new tab. */
export async function openCertificateFile(customerId, certificateId) {
  const r = await apiJson(`${API_BASE}/customers/${customerId}/tax_certificates/${certificateId}/download`);
  window.open(r.url, "_blank", "noopener");
}
